'use client'

import { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Send, MessageSquare, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { useCreatorNotes, useAddCreatorNote, useDeleteCreatorNote } from '@/hooks/use-creators'
import type { CreatorNote } from '@/lib/api/types'

// ─── Read-Only Feed (for Overview tab) ───────────────────────────────────────

interface NotesReadOnlyProps {
  creatorId: string
  maxVisible?: number
}

export function NotesReadOnly({ creatorId, maxVisible = 5 }: NotesReadOnlyProps) {
  const [showAll, setShowAll] = useState(false)
  const { data: result, isLoading } = useCreatorNotes(creatorId)
  const notes: CreatorNote[] = (result as CreatorNote[]) ?? []

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-1">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
    )
  }

  if (notes.length === 0) {
    return <p className="text-sm text-muted-foreground/60">Sem observações</p>
  }

  const visible = showAll ? notes : notes.slice(0, maxVisible)
  const hasMore = notes.length > maxVisible

  return (
    <div className="space-y-3">
      <div className="divide-y divide-border">
        {visible.map((note) => (
          <NoteEntryRow key={note.id} note={note} />
        ))}
      </div>
      {hasMore && !showAll && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setShowAll(true)}
        >
          Ver mais {notes.length - maxVisible} notas…
        </Button>
      )}
      {showAll && hasMore && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setShowAll(false)}
        >
          Mostrar menos
        </Button>
      )}
    </div>
  )
}

// ─── Editable Log (for Settings tab) ─────────────────────────────────────────

interface NotesLogEditorProps {
  creatorId: string
}

export function NotesLogEditor({ creatorId }: NotesLogEditorProps) {
  const [newNote, setNewNote] = useState('')
  const { data: result, isLoading } = useCreatorNotes(creatorId)
  const { mutate: addNote, isPending: isAdding } = useAddCreatorNote()
  const { mutate: deleteNote } = useDeleteCreatorNote()

  const notes: CreatorNote[] = (result as CreatorNote[]) ?? []

  const handleAdd = () => {
    const text = newNote.trim()
    if (!text) return
    addNote({ creatorId, text }, { onSuccess: () => setNewNote('') })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  const handleDelete = (noteId: string) => {
    deleteNote({ creatorId, noteId })
  }

  return (
    <div className="space-y-4">
      {/* Input */}
      <div className="space-y-2">
        <div className="relative">
          <Textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Adicione uma nota…"
            className="min-h-[80px] resize-none pr-12"
            disabled={isAdding}
          />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="absolute bottom-2 right-2 h-8 w-8 text-muted-foreground hover:text-primary disabled:opacity-40"
            onClick={handleAdd}
            disabled={!newNote.trim() || isAdding}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          <kbd className="rounded border bg-muted px-1 py-0.5 text-[10px]">Ctrl</kbd>
          {' + '}
          <kbd className="rounded border bg-muted px-1 py-0.5 text-[10px]">Enter</kbd>
          {' para enviar'}
        </p>
      </div>

      {/* Feed */}
      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-1">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-3 w-32" />
            </div>
          ))}
        </div>
      ) : notes.length > 0 ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Histórico ({notes.length})</p>
          <div className="divide-y divide-border rounded-md border">
            {notes.map((note) => (
              <NoteEntryRow
                key={note.id}
                note={note}
                className="px-3 py-2.5"
                onDelete={() => handleDelete(note.id)}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <MessageSquare className="h-8 w-8 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">Nenhuma nota ainda</p>
        </div>
      )}
    </div>
  )
}

// ─── Single Entry Row ────────────────────────────────────────────────────────

function NoteEntryRow({
  note,
  className,
  onDelete,
}: {
  note: CreatorNote
  className?: string
  onDelete?: () => void
}) {
  const timeLabel = note.createdAt
    ? formatDistanceToNow(new Date(note.createdAt), { addSuffix: true, locale: ptBR })
    : null

  return (
    <div className={`group flex items-start gap-2 py-2 ${className ?? ''}`}>
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{note.text}</p>
        <p className="text-xs text-muted-foreground">
          {note.authorName && <span className="font-medium">{note.authorName}</span>}
          {note.authorName && timeLabel && ' · '}
          {timeLabel}
        </p>
      </div>
      {onDelete && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  )
}
